'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
// Replaced lucide-react icons with emoji to avoid Runtime ReferenceError

interface User {
  id: string
  name: string
  username: string
  country?: string
  school?: string
  grade?: string
  rating: number
  maxRating: number
  problemsSolved: number
  contestsJoined: number
  rank: string
  mathLevel: string
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'rating' | 'problems' | 'recent'>('rating')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchUsers()
  }, [sortBy])

  useEffect(() => {
    filterUsers()
  }, [searchTerm, users])

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/users?sortBy=${sortBy}&limit=50`)
      const data = await response.json()
      if (response.ok) {
        setUsers(data)
        setFilteredUsers(data)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const filterUsers = () => {
    if (!searchTerm) {
      setFilteredUsers(users)
      return
    }

    const filtered = users.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.school?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredUsers(filtered)
  }

  const getRatingColor = (rating: number) => {
    if (rating >= 2400) return 'text-red-600'
    if (rating >= 2100) return 'text-yellow-600'
    if (rating >= 1900) return 'text-purple-600'
    if (rating >= 1600) return 'text-blue-600'
    if (rating >= 1400) return 'text-green-600'
    return 'text-gray-600'
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="bg-gray-600 h-8 w-64 rounded mb-8"></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="bg-gray-600 h-32 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4 drop-shadow-lg bg-gradient-to-r from-primary-400 to-purple-400 bg-clip-text text-transparent">
          Math Community
        </h1>
        <p className="text-gray-200 mb-6 text-lg">
          Discover and connect with fellow math enthusiasts from around the world
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 inline-block w-4 h-4 text-center">🔍</span>
            <input
              type="text"
              placeholder="Search users by name, username, country, or school..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="rating">Sort by Rating</option>
            <option value="problems">Sort by Problems Solved</option>
            <option value="recent">Recently Joined</option>
          </select>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-xl">👤</span>
          <p className="text-gray-200 text-lg">No users found</p>
          <p className="text-gray-300">Try adjusting your search terms</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => (
            <Link
              key={user.id}
              href={`/users/${user.username}`}
              className="card-energy p-6 interactive-hover super-glow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-white glow-text-secondary">
                    {user.name || user.username}
                  </h3>
                  <p className="text-gray-300 text-sm">@{user.username}</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                    user.rank === 'Candidate Master' ? 'bg-red-100 text-red-800' :
                    user.rank === 'Expert' ? 'bg-purple-100 text-purple-800' :
                    user.rank === 'Specialist' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.rank}
                  </span>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${getRatingColor(user.rating)}`}>
                    {user.rating}
                  </div>
                  <div className="text-xs text-gray-400">
                    Max: {user.maxRating}
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {user.country && (
                  <div className="flex items-center text-gray-300">
                    <span className="inline-block w-4 h-4 text-center mr-2">📍</span>
                    <span>{user.country}</span>
                  </div>
                )}
                {user.school && (
                  <div className="flex items-center text-gray-300">
                    <span className="inline-block w-4 h-4 text-center mr-2">🏫</span>
                    <span>{user.school}</span>
                    {user.grade && <span className="ml-1">({user.grade})</span>}
                  </div>
                )}
                <div className="flex items-center text-gray-300">
                  <span className="inline-block w-4 h-4 text-center mr-2">📊</span>
                  <span>{user.mathLevel}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-dark-600">
                <div className="flex justify-between text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-white">{user.problemsSolved}</div>
                    <div className="text-gray-300">Solved</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-white">{user.contestsJoined}</div>
                    <div className="text-gray-300">Contests</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-primary-400">{user.rating}</div>
                    <div className="text-gray-300">Rating</div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {filteredUsers.length >= 50 && (
        <div className="text-center mt-8">
          <button className="btn btn-secondary">
            Load More Users
          </button>
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <div className="card-energy p-6 text-center interactive-hover">
          <span className="text-xl">🏆</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Top Performers</h3>
          <p className="text-gray-200 text-sm">
            Connect with high-rated mathematicians and learn from their approaches.
          </p>
        </div>
        
        <div className="card-energy p-6 text-center interactive-hover">
          <span className="text-xl">🏫</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Students Worldwide</h3>
          <p className="text-gray-200 text-sm">
            Find peers from your school or grade level to study and compete together.
          </p>
        </div>
        
        <div className="card-energy p-6 text-center interactive-hover">
          <span className="text-xl">📊</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">All Math Levels</h3>
          <p className="text-gray-200 text-sm">
            From pre-algebra to calculus, find users at every mathematical level.
          </p>
        </div>
      </div>
    </div>
  )
}